/**
 * Współdzielone typy dla CMS variants registry — kontrakty dispatch'u
 * okien (`w_*`) i katalogów (`c_*`). Plik server-safe (BEZ `"use client"`)
 * żeby mógł być importowany zarówno przez Server Components (adapter)
 * jak i Client Components (variants.tsx, WindowRenderer, CatalogRenderer).
 */

import type { ComponentType, ReactNode } from "react";
import type { AreaSymbol } from "@/lib/layouts/types";
import type { CmsBackground } from "@/lib/api/cms-windows";
import type { FrontendUserSession } from "@/lib/api/session";

/**
 * Container component shape - reusable wrapper okna/katalogu (WDefault, WEmpty,
 * CDefault, CEmpty + future custom). Backend zwraca `container_symbol` z DB,
 * `resolveContainer` w `containers.tsx` mapuje na komponent tego typu.
 * Dispatch lambda w `windowVariants` / `catalogVariants` dostaje go jako
 * opcjonalny `ContainerOverride` i wrapuje content (fallback do hardcoded).
 *
 * Typ ducked dla wszystkich 4 reusable kontenerów - akceptuje opcjonalny title,
 * opcjonalny background, children.
 */
export type ContainerComponent = ComponentType<{
  title?: string;
  /** Opcjonalny podtytul renderowany pod `<h1>` w kontenerach ktore go obsluguja
   *  (CDefault). Kontenery bez naglowka (`CEmpty`) prop ignoruja. */
  subtitle?: string | null;
  background?: CmsBackground | null;
  children: ReactNode;
}>;

/**
 * Sygnatura renderera okna.
 *
 * @param content            Payload z REST API (typed per componentSymbol w funkcji).
 *                           Cast `content as WXxxContent` w body lambda.
 * @param title              `imp_cms_window.name` - header label gdy variant chce go pokazać.
 * @param background         `imp_cms_window.config[background]` resolved przez backend
 *                           z `m_site_backgrounds` (CSS-ready struct lub null).
 * @param area               `AreaSymbol` gdzie okno trafia - do edge case'ów gdy ten
 *                           sam variant ma drobną wariację per area.
 * @param ContainerOverride  Opcjonalny override wrappera z `container_symbol` z DB.
 *                           `resolveContainer(w.containerSymbol)` w adapterze
 *                           przekazuje konkretny komponent (np. `WDefault`).
 *                           Lambda używa go z fallback'iem do hardcoded:
 *                           `const Container = ContainerOverride ?? WEmpty;`
 *                           NULL = backend nie ma `container_symbol` lub nie
 *                           pasuje do żadnego zarejestrowanego w `containers.tsx`.
 */
export type WindowRenderer = (props: {
  content: unknown;
  title: string;
  background: CmsBackground | null;
  area: AreaSymbol;
  ContainerOverride?: ContainerComponent | null;
}) => ReactNode;

/**
 * Sygnatura renderera katalogu. Analogicznie do `WindowRenderer` ale:
 *   - katalog ma `title` z `imp_cms_catalog.name` (lub `name_header`).
 *   - dodatkowy prop `userSession` (SSR-resolved) dla komponentów reagujących
 *     na stan logowania (`c_user_accounts`). Inne ignorują.
 *   - `ContainerOverride` z `container_symbol` z `IAPI_catalogByPath` response.
 */
export type CatalogRenderer = (props: {
  content: unknown;
  title: string;
  /** Podtytul z `imp_cms_catalog.name_subtitle` - opcjonalny lead pod h1.
   *  Variant przekazuje do kontenera (CDefault renderuje, CEmpty ignoruje). */
  subtitle?: string | null;
  background: CmsBackground | null;
  userSession: FrontendUserSession | null;
  ContainerOverride?: ContainerComponent | null;
}) => ReactNode;

/**
 * Pełen zestaw window variants dla pojedynczego template'u CMS:
 *   - `defaults` — renderer per `componentSymbol` (np. `w_menu` -> lambda)
 *   - `areaOverrides` — opcjonalny override per `(componentSymbol, AreaSymbol)`
 */
export type WindowVariants = {
  defaults: Partial<Record<string, WindowRenderer>>;
  areaOverrides: Partial<Record<string, Partial<Record<AreaSymbol, WindowRenderer>>>>;
};

/**
 * Pełen zestaw catalog variants dla pojedynczego template'u CMS.
 * Struktura symetryczna do `WindowVariants` (areaOverrides w praktyce nieużywany
 * bo katalog renderuje się zawsze w strefie `catalogContent`).
 */
export type CatalogVariants = {
  defaults: Partial<Record<string, CatalogRenderer>>;
  areaOverrides: Partial<Record<string, Partial<Record<AreaSymbol, CatalogRenderer>>>>;
};

/**
 * Mapa dozwolonych obszarów per typ okna. Wartość = `[]` gdy brak constraint
 * (panel `UnpublishedWindowsPanel` traktuje to jako "okno nie da się wrzucić
 * nigdzie" — patrz `allowedAreas.ts`).
 */
export type WindowAllowedAreasMap = Partial<Record<string, ReadonlyArray<AreaSymbol>>>;

/**
 * Symbol templatu CMS — fallback `'default'` gdy `cmsConfig.template` jest null
 * lub nieznany. Każdy zarejestrowany template ma własny katalog
 * `templates/{symbol}/` z `windowVariants.tsx`, `catalogVariants.tsx`,
 * `allowedAreas.ts`.
 */
export const DEFAULT_TEMPLATE_SYMBOL = "default";
